import React, { useState, useEffect, useCallback, memo } from 'react';
import { fromJS } from 'immutable';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import connectOptions, { mergeProps } from '../../utils/connectOptions';
import stripOffChars from '../../utils/stripOffChars';
import Popups from '../../components/Popup';
import { unwrap } from '../../utils/ChangeSpy';
import * as TARGET from '../../actions/company/companyTarget';
import * as BUYER from '../../actions/company/buyer/marketing';
import getPhoneChanged, {
  checkPhoneIfNeeded,
  formatPhoneIfNeeded,
  checkExtensionIfNeeded,
  checkCountryIfNeeded,
} from '../services/phoneRules';
import { basicAccess } from '../../utils/checkPermissions';
import { PrettyErrorProvider } from '../../contexts/PrettyErrorContext';
import { usePopupContext } from '../../contexts/PopupContext';

const EditPhoneContactChannelPopupContainer = ({
  children,
  channel,
  isNew,
  contactId,
  channelIndex,
  channelId,
  pathPrefix,
  popup,
  phoneCodes,
  companyInfo,
  user,
  handleUpdateCompany,
  saveCompanyContactChannel,
  revertUpdate,
  loadCountryPhoneCodes,
  updateLastResearcher,
}) => {
  const context = usePopupContext();
  const [isBasicAccess, setIsBasicAccess] = useState(false);
  const [phoneErrors, setPhoneErrors] = useState({
    unformattedPhone: '',
    extension: '',
    country: '',
  });

  useEffect(() => {
    if (!phoneCodes.size) {
      loadCountryPhoneCodes();
    }

    if (isNew) {
      onChange({ target: { name: 'type', value: 'Direct' } });
    }
  }, []);

  useEffect(() => {
    setIsBasicAccess(basicAccess(user.getIn(['roles', 0, 'slug'])));

    context.onClosePopup(() => {
      const field = pathPrefix.slice(0, -1);

      revertUpdate({ field });
    });
  }, []);

  const setPhoneError = useCallback(({ phone, extension, country }) => {
    const errors = { unformattedPhone: '', extension: '', country: '' };

    if (phone === true) {
      errors.unformattedPhone =
        'Phone must be a valid North American area code and format (###-###-####) or +[numbers, spaces and dashes] if international';
    }

    if (extension === true) {
      errors.extension = 'The extension field is required when type is Extension.';
    }

    if (country === true) {
      errors.country = 'The country field is required when type is International.';
    }

    setPhoneErrors(errors);
  }, []);

  const onErrorClose = useCallback(() => {
    setPhoneError({});
  }, [setPhoneError]);

  const onChange = useCallback(
    event => {
      const { name, value } = event.target;

      if (name === 'country') {
        const index = phoneCodes.findIndex(p => p.get('code') === value);

        if (index > -1) {
          handleUpdateCompany({
            name: `${pathPrefix}code`,
            value: phoneCodes.getIn([index, 'dialCode']),
          });
        }
      }

      return handleUpdateCompany({
        name: `${pathPrefix}${name}`,
        value: name === 'extension' ? stripOffChars(value, '', /[^\d]/gi) : value,
      });
    },
    [handleUpdateCompany, pathPrefix, phoneCodes],
  );

  const onUndo = useCallback(() => {
    onChange({ target: { name: 'type', value: 'Direct' } });
    onChange({ target: { name: 'code', value: '' } });
    onChange({ target: { name: 'extension', value: '' } });
    onChange({ target: { name: 'country', value: '' } });
  }, [onChange]);

  const phoneOnBlur = useCallback(
    ({ target: { value } }) => {
      const type = unwrap(channel.get('type'));

      if (!checkPhoneIfNeeded(type, value)) {
        setPhoneError({ phone: true });
      } else {
        onChange({
          target: {
            name: 'unformattedPhone',
            value: formatPhoneIfNeeded(type, value),
          },
        });
        setPhoneError({ phone: false });
      }
    },
    [channel, onChange, setPhoneError],
  );

  const extensionOnBlur = useCallback(
    ({ target: { value } }) => {
      const type = unwrap(channel.get('type'));

      if (!checkExtensionIfNeeded(type, value)) {
        setPhoneError({ extension: true });
      } else {
        setPhoneError({ extension: false });
      }
    },
    [channel, setPhoneError],
  );

  const countryOnBlur = useCallback(
    ({ target: { value } }) => {
      const type = unwrap(channel.get('type'));

      if (!checkCountryIfNeeded(type, value)) {
        setPhoneError({ extension: true });
      } else {
        setPhoneError({ extension: false });
      }
    },
    [channel, setPhoneError],
  );

  const onBlur = useCallback(
    event => {
      if (event.target.name === 'unformattedPhone') {
        phoneOnBlur(event);
      } else if (event.target.name === 'extension') {
        extensionOnBlur(event);
      } else if (event.target.name === 'country') {
        countryOnBlur(event);
      }
    },
    [phoneOnBlur, extensionOnBlur, countryOnBlur],
  );

  const onSave = useCallback(() => {
    const type = unwrap(channel.get('type'));
    const phone = unwrap(channel.get('unformattedPhone'));
    const extension = unwrap(channel.get('extension'));
    const country = unwrap(channel.get('country'));
    const phoneCheck = checkPhoneIfNeeded(type, phone);
    const extensionCheck = checkExtensionIfNeeded(type, extension);
    const countryCheck = checkCountryIfNeeded(type, country);
    const companyId = popup.getIn(['props', 'params', 'companyId']);
    const mode = popup.getIn(['props', 'mode']);

    if (phoneCheck && extensionCheck && countryCheck) {
      const body = getPhoneChanged(channel);

      saveCompanyContactChannel(
        {
          companyId,
          isNew,
          contactId,
          channelType: 'phones',
          channelId,
          channelIndex,
          body,
        },
        () => {
          context.closePopup();

          if (isBasicAccess && mode !== 'buyer') {
            updateLastResearcher(user.get('userName'));
          }
        },
      );
      setPhoneError({});
    } else {
      setPhoneError({
        phone: !phoneCheck,
        extension: !extensionCheck,
        country: !countryCheck,
      });
    }
  }, [
    channel,
    isNew,
    contactId,
    channelIndex,
    channelId,
    popup,
    saveCompanyContactChannel,
    updateLastResearcher,
    user,
    isBasicAccess,
    setPhoneError,
    context.closePopup,
  ]);

  return (
    <PrettyErrorProvider inputErrors={fromJS(phoneErrors)} onErrorClose={onErrorClose}>
      <>
        <Popups.EditPhoneContactChannelPopup
          channel={channel}
          companyInfo={companyInfo}
          isNew={isNew}
          onBlur={onBlur}
          onChange={onChange}
          onSave={onSave}
          onUndo={onUndo}
          phoneCodes={phoneCodes}
        />
        {children}
      </>
    </PrettyErrorProvider>
  );
};

function mapStateToProps(state, props) {
  const openedContact = props.popup.getIn(['props', 'openedContact']);
  const channelType = 'phones';
  const channelIndex = props.popup.getIn(['props', 'channelIndex']);
  const channelId = props.popup.getIn(['props', 'channelId']);
  const companyInfo = props.popup.getIn(['props', 'companyInfo']);
  const isBuyer = props.popup.getIn(['props', 'mode']) === 'buyer';
  const root = isBuyer ? state.targetCompany.buyer.marketing : state.targetCompany.target;
  const contact = root.getIn(['contacts', openedContact]);
  const contactId = contact.get('id');
  const isNew = !parseInt(channelId, 10);
  const pathPrefix = isNew ? `newData.${channelType}.` : `contacts.${openedContact}.${channelType}.${channelIndex}.`;
  const channel = root.getIn(pathPrefix.split('.').slice(0, -1));
  const phoneCodes = root.get('phoneCodes');

  return {
    isNew,
    channel,
    channelId,
    channelIndex,
    contactId,
    pathPrefix,
    phoneCodes,
    contact,
    companyInfo,
    user: state.auth.get('user', null),
  };
}

function mapDispatchToProps(dispatch, props) {
  const isBuyer = props.popup.getIn(['props', 'mode']) === 'buyer';

  if (isBuyer) {
    const { handleUpdateCompany, saveCompanyContactChannel } = BUYER;

    return bindActionCreators(
      {
        handleUpdateCompany,
        saveCompanyContactChannel,
        revertUpdate: TARGET.revertUpdate,
        loadCountryPhoneCodes: TARGET.loadCountryPhoneCodes,
      },
      dispatch,
    );
  }

  const {
    handleUpdateCompany,
    saveCompanyContactChannel,
    revertUpdate,
    loadCountryPhoneCodes,
    updateLastResearcher,
  } = TARGET;

  return bindActionCreators(
    {
      handleUpdateCompany,
      saveCompanyContactChannel,
      revertUpdate,
      loadCountryPhoneCodes,
      updateLastResearcher,
    },
    dispatch,
  );
}

export { EditPhoneContactChannelPopupContainer };

const Connected = connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps,
  connectOptions,
)(memo(EditPhoneContactChannelPopupContainer));

export default Connected;
